import { LoadingOutlined } from "@ant-design/icons";
import { InteractionType } from "@azure/msal-browser";
import {
  MsalAuthenticationResult,
  MsalAuthenticationTemplate,
  MsalProvider,
} from "@azure/msal-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { App as AntdApp, Button, ConfigProvider } from "antd";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter as Router } from "react-router-dom";
import { Toaster } from "react-hot-toast";


import { antdThemeConfig } from "@/config/antd";
import { msalInstance } from "@/config/msal";
import { queryClient } from "@/lib/reactQuery";
import { AppProviderProps } from "@/types";
import { MSALAUTH } from "@/utils/auth";

const ErrorFallback = () => {
  return (
    <div
      className="flex h-screen w-screen flex-col items-center justify-center text-red-500"
      role="alert"
    >
      <h2 className="text-lg font-semibold">Ooops, something went wrong</h2>
      <Button
        type="primary"
        onClick={() => window.location.assign(window.location.origin)}
      >
        Refresh
      </Button>
    </div>
  );
};

export const InProgressComponent = () => {
  return (
    <div className="flex h-full items-center justify-center gap-x-1.5 p-[100px]">
      <LoadingOutlined />
      <span>Loading...</span>
    </div>
  );
};

const ErrorComponent: React.FC<MsalAuthenticationResult> = ({ error }) => {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-[100px]">
      <p>
        This is a protected page and the following error occurred during
        authentication: <strong>{error?.errorCode}</strong>
      </p>
      <button
        className="rounded bg-gray-200 p-2 hover:bg-gray-100 hover:font-medium"
        onClick={() => {
          MSALAUTH.logOut();
        }}
      >
        Go back
      </button>
    </div>
  );
};

export const AppProvider = ({ children }: AppProviderProps) => {
  return (
    <React.Suspense fallback={<InProgressComponent />}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              iconTheme: {
                primary: '#52c41a',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ff4d4f',
                secondary: '#fff',
              },
            },
          }}
        />
        <MsalProvider instance={msalInstance}>
          <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            loadingComponent={InProgressComponent}
            errorComponent={ErrorComponent}
          >
            <QueryClientProvider client={queryClient}>
              <ConfigProvider theme={antdThemeConfig}>
                  <AntdApp>
                    <Router>{children}</Router>
                </AntdApp>
              </ConfigProvider>
            </QueryClientProvider>
          </MsalAuthenticationTemplate>
        </MsalProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
